// src/pages/Sanitary/SanitarySolutionEditorPage.jsx
// =============================================================================
// Professional create/edit screen for versioned working-solution recipes.
// =============================================================================

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import { listSanitaryProducts } from "../../services/sanitaryProductService";
import { fetchUnitsOfMeasure } from "../../services/unitsOfMeasureService";
import { fetchStockLocations } from "../../services/stockService";
import {
  createSanitarySolutionDefinition,
  deleteSanitarySolutionDefinition,
  getSanitarySolutionDefinition,
  previewSanitarySolutionPreparation,
  saveSanitarySolutionDefinitionEditor,
} from "../../services/sanitarySolutionService";

import "../Stock/StockMasterDataPage.css";
import "./SanitaryProductsPage.css";
import "./SanitarySolutionsPage.css";

const todayIso = () => new Date().toISOString().slice(0, 10);
const STATUSES = ["draft", "active", "inactive", "archived"];

function normalizeRole(role) { return String(role || "").trim().toLowerCase(); }
function asArray(value) { return Array.isArray(value) ? value : []; }
function productFromRow(row) { return row?.product || row || null; }
function sanitaryProfileFrom(value) {
  return value?.profile || value?.sanitaryProfile || value?.sanitary_profile || value?.product?.sanitaryProfile || value?.product?.sanitary_profile || null;
}
function isConcentrate(row) {
  const profile = sanitaryProfileFrom(row);
  const form = String(profile?.form || "").toLowerCase();
  return Boolean(profile?.is_active !== false && (form === "concentrate" || profile?.requires_dilution === true));
}
function formatQty(value) {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : String(value);
}
function locationLabel(location) { return [location?.code, location?.name].filter(Boolean).join(" · ") || "—"; }
function productLabel(row) {
  const product = productFromRow(row);
  return [product?.name_public, product?.variant_label, product?.sku ? `SKU ${product.sku}` : null].filter(Boolean).join(" · ") || "—";
}
function nextDay(dateIso) {
  if (!dateIso) return todayIso();
  const d = new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
function maxIso(a, b) { return String(a || "") > String(b || "") ? a : b; }

const emptyForm = () => ({
  code: "",
  name: "",
  status: "draft",
  base_product_id: "",
  solution_volume: "",
  solution_unit_code: "l",
  concentration_percent: "",
  working_solution_valid_hours: "",
  default_location_id: "",
  valid_from: todayIso(),
  valid_to: "",
  definition_comment: "",
  version_comment: "",
});

function formFromDefinition(definition) {
  const v = definition?.latestVersion || definition?.currentVersion || definition?.versions?.[0] || {};
  return {
    code: definition?.code || "",
    name: definition?.name || "",
    status: definition?.status || "draft",
    base_product_id: v.base_product_id ? String(v.base_product_id) : "",
    solution_volume: v.solution_volume ?? "",
    solution_unit_code: v.solution_unit_code || "l",
    concentration_percent: v.concentration_percent ?? "",
    working_solution_valid_hours: v.working_solution_valid_hours ?? "",
    default_location_id: v.default_location_id ? String(v.default_location_id) : "",
    valid_from: v.valid_from || todayIso(),
    valid_to: v.valid_to || "",
    definition_comment: definition?.comment || "",
    version_comment: v.comment || "",
  };
}

function Section({ number, title, subtitle, children }) {
  return (
    <section className="sanitary-solution-editor-section">
      <div className="sanitary-solution-editor-section__heading">
        <span className="sanitary-solution-editor-section__number">{number}</span>
        <div><h2>{title}</h2>{subtitle ? <p>{subtitle}</p> : null}</div>
      </div>
      <div className="sanitary-solution-editor-section__body">{children}</div>
    </section>
  );
}

export default function SanitarySolutionEditorPage() {
  const { definitionId } = useParams();
  const isNew = !definitionId;
  const navigate = useNavigate();
  const { t } = useTranslation(["sanitary", "common"]);
  const { authState } = useContext(AuthContext);
  const role = normalizeRole(authState?.user?.role);
  const canWrite = role === "superuser" || role === "reception";

  const [definition, setDefinition] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState([]);
  const [preview, setPreview] = useState(null);
  const [editorMode, setEditorMode] = useState("update_current");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const concentrateProducts = useMemo(() => products.filter(isConcentrate), [products]);
  const latest = definition?.latestVersion || definition?.versions?.[0] || null;
  const currentUsed = Boolean(latest?.usage?.business_used || definition?.editor?.current_version_used);
  const versionLocked = !isNew && editorMode !== "new_version" && currentUsed;
  const canDelete = Boolean(definition?.usage?.can_delete || definition?.editor?.can_delete);
  const referenced = Boolean(definition?.usage?.referenced || definition?.editor?.referenced_by_rules);

  useEffect(() => {
    let active = true;
    Promise.all([
      listSanitaryProducts({ profiled_only: "true", product_active: "true", limit: 500 }),
      fetchStockLocations(),
      fetchUnitsOfMeasure({ active: true }),
    ]).then(([productRows, locationRows, unitRows]) => {
      if (!active) return;
      setProducts(asArray(productRows));
      setLocations(asArray(locationRows));
      setUnits(asArray(unitRows));
    }).catch((err) => { if (active) setError(err?.message || t("sanitary:solutions.messages.masterDataFailed")); });
    return () => { active = false; };
  }, [t]);

  useEffect(() => {
    if (isNew) return undefined;
    let active = true;
    setLoading(true);
    getSanitarySolutionDefinition(definitionId, { business_date: todayIso() })
      .then((data) => {
        if (!active) return;
        setDefinition(data);
        setForm(formFromDefinition(data));
        setEditorMode("update_current");
      })
      .catch((err) => { if (active) setError(err?.message || t("sanitary:solutions.messages.loadFailed")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [definitionId, isNew, t]);

  useEffect(() => {
    if (!form.base_product_id || !form.solution_volume || !form.solution_unit_code || !form.concentration_percent) {
      setPreview(null);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      previewSanitarySolutionPreparation({
        base_product_id: form.base_product_id,
        solution_volume: form.solution_volume,
        solution_unit_code: form.solution_unit_code,
        concentration_percent: form.concentration_percent,
      }).then((data) => { if (active) setPreview(data); }).catch(() => { if (active) setPreview(null); });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [form.base_product_id, form.solution_volume, form.solution_unit_code, form.concentration_percent]);

  function patch(partial) { setError(""); setSuccess(""); setForm((prev) => ({ ...prev, ...partial })); }

  function handleProductChange(productId) {
    const row = concentrateProducts.find((item) => String(productFromRow(item)?.id) === String(productId));
    const product = productFromRow(row);
    const profile = sanitaryProfileFrom(row);
    patch({
      base_product_id: productId,
      concentration_percent: profile?.default_concentration_percent ?? form.concentration_percent,
      working_solution_valid_hours: profile?.working_solution_valid_hours ?? form.working_solution_valid_hours,
      solution_unit_code: product?.content_unit_code || product?.stock_unit_code || form.solution_unit_code || "l",
    });
  }

  function payload(statusOverride = null) {
    return {
      code: form.code || null,
      name: form.name,
      status: statusOverride || form.status || "draft",
      base_product_id: form.base_product_id,
      solution_volume: form.solution_volume,
      solution_unit_code: form.solution_unit_code,
      concentration_percent: form.concentration_percent,
      working_solution_valid_hours: form.working_solution_valid_hours || null,
      default_location_id: form.default_location_id || null,
      valid_from: form.valid_from,
      valid_to: form.valid_to || null,
      definition_comment: form.definition_comment || null,
      version_comment: form.version_comment || null,
    };
  }

  async function save(statusOverride = null) {
    if (!canWrite || saving) return;
    setSaving(true); setError(""); setSuccess("");
    try {
      if (isNew) {
        const created = await createSanitarySolutionDefinition(payload(statusOverride || "draft"));
        setSuccess(t("sanitary:solutions.professional.editor.created"));
        navigate(`/sanitary/solutions/definitions/${created.id}`, { replace: true });
        return;
      }
      const mode = editorMode === "new_version" ? "new_version" : (versionLocked ? "header_only" : "update_current");
      const updated = await saveSanitarySolutionDefinitionEditor(definitionId, {
        ...payload(statusOverride),
        editor_mode: mode,
        version_id: latest?.id || null,
      });
      setDefinition(updated);
      setForm(formFromDefinition(updated));
      setEditorMode("update_current");
      setSuccess(mode === "new_version" ? t("sanitary:solutions.professional.editor.versionCreated") : t("sanitary:solutions.professional.editor.saved"));
    } catch (err) {
      setError(err?.message || t("sanitary:solutions.messages.definitionUpdateFailed"));
    } finally { setSaving(false); }
  }

  function startNewVersion() {
    if (!latest) return;
    const earliest = latest?.usage?.last_used_business_date ? nextDay(latest.usage.last_used_business_date) : todayIso();
    setEditorMode("new_version");
    setForm((prev) => ({ ...prev, valid_from: maxIso(todayIso(), earliest), valid_to: "", version_comment: "" }));
    setError(""); setSuccess("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleDelete() {
    if (!canWrite || !canDelete || !definition) return;
    if (!window.confirm(t("sanitary:solutions.professional.editor.deleteConfirm", { name: definition.name }))) return;
    setSaving(true); setError("");
    try {
      await deleteSanitarySolutionDefinition(definition.id);
      navigate("/sanitary/solutions", { replace: true });
    } catch (err) {
      setError(err?.message || t("sanitary:solutions.professional.editor.deleteFailed"));
      setSaving(false);
    }
  }

  if (loading) return <main className="stock-md-page"><div className="stock-md-card">{t("sanitary:solutions.messages.loading")}</div></main>;

  return (
    <main className="stock-md-page sanitary-solution-editor">
      <header className="stock-md-header sanitary-solution-editor__header">
        <div>
          <button type="button" className="sanitary-solution-editor__back" onClick={() => navigate("/sanitary/solutions")}>← {t("sanitary:solutions.professional.editor.back")}</button>
          <p className="stock-md-eyebrow">{t("sanitary:solutions.eyebrow")}</p>
          <h1 className="stock-md-title">{isNew ? t("sanitary:solutions.professional.editor.createTitle") : t("sanitary:solutions.professional.editor.editTitle")}</h1>
          <p className="stock-md-subtitle">{isNew ? t("sanitary:solutions.professional.editor.createSubtitle") : definition?.name}</p>
        </div>
        {!isNew && definition ? <div className="sanitary-solution-editor__meta"><span className={`sanitary-solution-status sanitary-solution-status--${definition.status}`}>{t(`sanitary:solutions.professional.definitionStatuses.${definition.status}`, { defaultValue: definition.status })}</span><span>v{latest?.version_no || "—"}</span></div> : null}
      </header>

      {error ? <div className="stock-md-error">{error}</div> : null}
      {success ? <div className="stock-md-success">{success}</div> : null}
      {!canWrite ? <div className="stock-md-info-box">{t("sanitary:solutions.professional.editor.readOnly")}</div> : null}

      {referenced && !currentUsed && editorMode !== "new_version" ? (
        <div className="sanitary-solution-editor-warning">
          <strong>{t("sanitary:solutions.professional.editor.referencedTitle")}</strong>
          <p>{t("sanitary:solutions.professional.editor.referencedText", { count: definition?.usage?.rule_versions || 0 })}</p>
        </div>
      ) : null}

      {versionLocked ? (
        <div className="sanitary-solution-editor-lock">
          <div><strong>🔒 {t("sanitary:solutions.professional.editor.lockedTitle")}</strong><p>{t("sanitary:solutions.professional.editor.lockedText", { date: latest?.usage?.last_used_business_date || "—" })}</p></div>
          {canWrite ? <button type="button" className="stock-md-button stock-md-button--primary" onClick={startNewVersion}>{t("sanitary:solutions.professional.editor.scheduleVersion")}</button> : null}
        </div>
      ) : null}

      {editorMode === "new_version" ? <div className="sanitary-solution-editor-version-banner"><div><strong>{t("sanitary:solutions.professional.editor.newVersionMode")}</strong><p>{t("sanitary:solutions.professional.editor.newVersionModeText")}</p></div><button type="button" className="stock-md-button" onClick={() => { setEditorMode("update_current"); setForm(formFromDefinition(definition)); }}>{t("common:actions.cancel", { defaultValue: "Anuluj" })}</button></div> : null}

      <div className="sanitary-solution-editor__form">
        <Section number="1" title={t("sanitary:solutions.professional.editor.sections.basic")}>
          <div className="stock-md-grid stock-md-grid--three">
            <label className="stock-md-field"><span>{t("sanitary:solutions.definitions.code")}</span><input value={form.code} onChange={(e) => patch({ code: e.target.value })} disabled={!canWrite || saving} /></label>
            <label className="stock-md-field stock-md-field--wide"><span>{t("sanitary:solutions.definitions.name")}</span><input value={form.name} onChange={(e) => patch({ name: e.target.value })} disabled={!canWrite || saving} required /></label>
            <label className="stock-md-field"><span>{t("sanitary:solutions.professional.editor.status")}</span><select value={form.status} onChange={(e) => patch({ status: e.target.value })} disabled={!canWrite || saving}>{STATUSES.map((s) => <option key={s} value={s}>{t(`sanitary:solutions.professional.definitionStatuses.${s}`, { defaultValue: s })}</option>)}</select></label>
          </div>
        </Section>

        <Section number="2" title={t("sanitary:solutions.professional.editor.sections.recipe")} subtitle={t("sanitary:solutions.professional.editor.sections.recipeHint")}>
          <div className="stock-md-grid stock-md-grid--three">
            <label className="stock-md-field stock-md-field--wide"><span>{t("sanitary:solutions.form.baseProduct")}</span><select value={form.base_product_id} onChange={(e) => handleProductChange(e.target.value)} disabled={!canWrite || saving || versionLocked} required><option value="">{t("sanitary:common.select")}</option>{concentrateProducts.map((row) => { const product = productFromRow(row); return <option key={product.id} value={product.id}>{productLabel(row)}</option>; })}</select></label>
            <label className="stock-md-field"><span>{t("sanitary:solutions.definitions.solutionVolume")}</span><input type="number" min="0.000001" step="0.000001" value={form.solution_volume} onChange={(e) => patch({ solution_volume: e.target.value })} disabled={!canWrite || saving || versionLocked} required /></label>
            <label className="stock-md-field"><span>{t("sanitary:solutions.definitions.solutionUnit")}</span><select value={form.solution_unit_code} onChange={(e) => patch({ solution_unit_code: e.target.value })} disabled={!canWrite || saving || versionLocked}>{units.map((u) => <option key={u.code} value={u.code}>{u.symbol || u.code}</option>)}</select></label>
            <label className="stock-md-field"><span>{t("sanitary:solutions.definitions.concentration")}</span><input type="number" min="0.0001" max="99.9999" step="0.0001" value={form.concentration_percent} onChange={(e) => patch({ concentration_percent: e.target.value })} disabled={!canWrite || saving || versionLocked} required /></label>
          </div>
          {preview ? <div className="sanitary-solution-recipe-preview"><strong>{t("sanitary:solutions.professional.editor.previewTitle")}</strong><div className="sanitary-solution-recipe-preview__values"><span>{formatQty(preview.solutionVolume)} {preview.solutionUnitCode} @ {formatQty(preview.concentrationPercent)}%</span><span><b>{formatQty(preview.concentrateQuantity)} {preview.concentrateUnitCode}</b> {t("sanitary:solutions.adHoc.concentrate")}</span><span>{formatQty(preview.waterQuantity)} {preview.waterUnitCode} {t("sanitary:solutions.adHoc.water")}</span></div></div> : null}
        </Section>

        <Section number="3" title={t("sanitary:solutions.professional.editor.sections.validity")}>
          <div className="stock-md-grid stock-md-grid--three">
            <label className="stock-md-field"><span>{t("sanitary:solutions.definitions.validHours")}</span><input type="number" min="1" step="1" value={form.working_solution_valid_hours} onChange={(e) => patch({ working_solution_valid_hours: e.target.value })} disabled={!canWrite || saving || versionLocked} /></label>
            <label className="stock-md-field"><span>{t("sanitary:solutions.definitions.validFrom")}</span><input type="date" value={form.valid_from} onChange={(e) => patch({ valid_from: e.target.value })} disabled={!canWrite || saving || versionLocked} required /></label>
            <label className="stock-md-field"><span>{t("sanitary:solutions.definitions.validTo")}</span><input type="date" value={form.valid_to} onChange={(e) => patch({ valid_to: e.target.value })} disabled={!canWrite || saving || versionLocked} /></label>
          </div>
        </Section>

        <Section number="4" title={t("sanitary:solutions.professional.editor.sections.execution")}>
          <div className="stock-md-grid stock-md-grid--three"><label className="stock-md-field stock-md-field--wide"><span>{t("sanitary:solutions.definitions.defaultLocation")}</span><select value={form.default_location_id} onChange={(e) => patch({ default_location_id: e.target.value })} disabled={!canWrite || saving || versionLocked}><option value="">—</option>{locations.map((l) => <option key={l.id} value={l.id}>{locationLabel(l)}</option>)}</select></label></div>
        </Section>

        <Section number="5" title={t("sanitary:solutions.professional.editor.sections.notes")}>
          <div className="stock-md-grid stock-md-grid--two"><label className="stock-md-field"><span>{t("sanitary:solutions.professional.editor.definitionComment")}</span><textarea rows="3" value={form.definition_comment} onChange={(e) => patch({ definition_comment: e.target.value })} disabled={!canWrite || saving} /></label><label className="stock-md-field"><span>{t("sanitary:solutions.professional.editor.versionComment")}</span><textarea rows="3" value={form.version_comment} onChange={(e) => patch({ version_comment: e.target.value })} disabled={!canWrite || saving || versionLocked} /></label></div>
        </Section>

        {!isNew && definition?.versions?.length ? <details className="sanitary-solution-version-history"><summary>{t("sanitary:solutions.professional.editor.versionHistory", { count: definition.versions.length })}</summary><div className="stock-md-table-wrapper"><table className="stock-md-table"><thead><tr><th>v</th><th>{t("sanitary:solutions.definitions.validFrom")}</th><th>{t("sanitary:solutions.definitions.validTo")}</th><th>{t("sanitary:solutions.definitions.tableRecipe")}</th><th>{t("sanitary:solutions.professional.editor.usage")}</th></tr></thead><tbody>{definition.versions.map((v) => <tr key={v.id}><td>v{v.version_no}</td><td>{v.valid_from}</td><td>{v.valid_to || "∞"}</td><td>{formatQty(v.solution_volume)} {v.solution_unit_symbol || v.solution_unit_code} / {formatQty(v.concentration_percent)}%</td><td>{v.usage?.business_used ? t("sanitary:solutions.professional.editor.used", { count: Number(v.usage.consumption_entries || 0) + Number(v.usage.preparations || 0) }) : t("sanitary:solutions.professional.editor.unused")}</td></tr>)}</tbody></table></div></details> : null}
      </div>

      <footer className="sanitary-solution-editor__footer">
        {!isNew && canWrite && canDelete ? <button type="button" className="stock-md-button sanitary-solution-button--danger" onClick={handleDelete} disabled={saving}>{t("common:actions.delete", { defaultValue: "Usuń" })}</button> : null}
        <span className="sanitary-solution-editor__footer-spacer" />
        <button type="button" className="stock-md-button" onClick={() => navigate("/sanitary/solutions")} disabled={saving}>{t("common:actions.cancel", { defaultValue: "Anuluj" })}</button>
        {canWrite ? isNew ? <><button type="button" className="stock-md-button" onClick={() => save("draft")} disabled={saving}>{t("sanitary:solutions.professional.editor.saveDraft")}</button><button type="button" className="stock-md-button stock-md-button--primary" onClick={() => save("active")} disabled={saving}>{t("sanitary:solutions.professional.editor.saveAndActivate")}</button></> : <button type="button" className="stock-md-button stock-md-button--primary" onClick={() => save()} disabled={saving}>{editorMode === "new_version" ? t("sanitary:solutions.professional.editor.createVersion") : t("common:actions.save", { defaultValue: "Zapisz" })}</button> : null}
      </footer>
    </main>
  );
}
